import React, { useEffect, useMemo, useState } from 'react';
import { LuTrash2 } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';

import { FeatureFlag, Role } from '@ndvr/auth-api';
import {
    Allocation,
    AllocationType,
    AllocationWithUsage,
    GUID,
    Strategy,
    StrategyType,
    StrategyWithUsage,
} from '@ndvr/portfolio-api';

import { BulkSelectionBar } from '@/components/BulkSelectionBar';
import { ConfirmDeleteModal } from '@/components/ConfirmDeleteModal';
import { ConfirmModal } from '@/components/ConfirmModal';
import { Button } from '@/components/ui/button';
import { Page, PageContent, PageTopBar } from '@/components/ui/page-layout';
import { useConfirmAction } from '@/hooks/useConfirmAction';
import { cn } from '@/utils/cn';
import { useUserHasRole } from 'views/admin/dev-queries';
import { useFeatureFlag } from 'views/auth/useFeatureFlag';

import { useDeleteAllocation, useListAllocations } from './allocations/allocation-queries';
import { isBlackrockAllocation } from './allocations/allocation-utils';
import { AllocationDraft, resolveClonedAllocationType } from './allocations/cloneAllocationType';
import { ModelsSection } from './allocations/ModelsSection';
import { EditModelDialog } from './allocations/new-model/EditModelDialog';
import { LibraryFilterBar, LibraryFilterKey, LibrarySortKey } from './LibraryFilterBar';
import { LibraryTitleBar } from './LibraryTitleBar';
import { EditStrategyDialog } from './strategies/EditStrategyDialog';
import { StrategiesSection } from './strategies/StrategiesSection';
import { FormValues as StrategyFormValues } from './strategies/strategy-form-schema';
import {
    useCreateStrategy,
    useDeleteStrategy,
    useListStrategies,
    useUpdateStrategy,
} from './strategies/strategy-queries';
import { isBlackrockStrategy, isSelectableStrategy, StrategyDraft } from './strategies/strategy-utils';

export const ModelLibraryPage: React.FC = () => {
    const navigate = useNavigate();
    const [filter, setFilter] = useState<LibraryFilterKey>('all');
    const [search, setSearch] = useState('');
    const [sort, setSort] = useState<LibrarySortKey>('name');

    const [selectedModelGUIDs, setSelectedModelGUIDs] = useState<Set<GUID<Allocation>>>(new Set());
    const [selectedStrategyGUIDs, setSelectedStrategyGUIDs] = useState<Set<GUID<Strategy>>>(new Set());
    const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);

    useEffect(() => {
        setSelectedModelGUIDs(new Set());
        setSelectedStrategyGUIDs(new Set());
    }, [filter, search]);

    const { data: hasSupportRole } = useUserHasRole(Role.SUPPORT_USER);
    const isBlackrockEnabled = !!useFeatureFlag(FeatureFlag.CC_BLACKROCK_STRATEGIES);

    useEffect(() => {
        if (filter === 'blackrock' && !isBlackrockEnabled) {
            setFilter('all');
        }
    }, [filter, isBlackrockEnabled]);
    const { data: allocations = [], isLoading: isLoadingAllocations } = useListAllocations();
    const { data: strategies = [], isLoading: isLoadingStrategies } = useListStrategies();

    const [isModelDialogOpen, setIsModelDialogOpen] = useState(false);
    const [editingAllocation, setEditingAllocation] = useState<Allocation | AllocationDraft | undefined>(undefined);
    const { mutate: deleteAllocation } = useDeleteAllocation();

    const handleEditModel = (allocation: Allocation) => {
        setEditingAllocation(allocation);
        setIsModelDialogOpen(true);
    };

    const handleCloneModel = (allocation: Allocation) => {
        const draft: AllocationDraft = {
            ...allocation,
            allocationGUID: undefined,
            allocationName: `${allocation.allocationName} (Copy)`,
            allocationType: resolveClonedAllocationType(allocation.allocationType, !!hasSupportRole),
        };
        setEditingAllocation(draft);
        setIsModelDialogOpen(true);
    };

    const handleCancelModel = () => {
        setIsModelDialogOpen(false);
        setEditingAllocation(undefined);
    };

    const modelDelete = useConfirmAction<AllocationWithUsage>((allocation) =>
        deleteAllocation(allocation.allocationGUID),
    );

    const [isStrategyDialogOpen, setIsStrategyDialogOpen] = useState(false);
    const [editingStrategy, setEditingStrategy] = useState<Strategy | StrategyDraft | undefined>(undefined);

    const { mutate: createStrategy, isPending: isCreatingStrategy } = useCreateStrategy();
    const { mutate: updateStrategy, isPending: isUpdatingStrategy } = useUpdateStrategy();
    const { mutate: deleteStrategy } = useDeleteStrategy();
    const isSavingStrategy = isCreatingStrategy || isUpdatingStrategy;
    const strategyDelete = useConfirmAction<StrategyWithUsage>((strategy) => deleteStrategy(strategy.strategyGUID));

    const handleNewStrategy = () => {
        navigate('/model-library/strategies/new');
    };

    const handleNewModel = () => {
        navigate('/model-library/models/new');
    };

    const handleEditStrategy = (strategy: Strategy) => {
        setEditingStrategy(strategy);
        setIsStrategyDialogOpen(true);
    };

    const handleCloneStrategy = (strategy: Strategy) => {
        const draft: StrategyDraft = {
            ...strategy,
            strategyGUID: undefined,
            strategyName: `${strategy.strategyName} (Copy)`,
        };
        setEditingStrategy(draft);
        setIsStrategyDialogOpen(true);
    };

    const handleCancelStrategy = () => {
        setIsStrategyDialogOpen(false);
        setEditingStrategy(undefined);
    };

    const handleSaveStrategy = (data: StrategyFormValues) => {
        if (editingStrategy?.strategyGUID) {
            updateStrategy(
                {
                    strategyGUID: editingStrategy.strategyGUID,
                    request: { strategyName: data.strategyName, assetAllocations: data.assetAllocations },
                },
                {
                    onSuccess: () => {
                        setIsStrategyDialogOpen(false);
                        setEditingStrategy(undefined);
                    },
                },
            );
        } else {
            createStrategy(
                {
                    strategyName: data.strategyName,
                    strategyType: data.strategyType,
                    assetAllocations: data.assetAllocations,
                },
                {
                    onSuccess: () => {
                        setIsStrategyDialogOpen(false);
                        setEditingStrategy(undefined);
                    },
                },
            );
        }
    };

    const toggleModelSelected = (allocationGUID: GUID<Allocation>) => {
        setSelectedModelGUIDs((prev) => {
            const next = new Set(prev);
            next.has(allocationGUID) ? next.delete(allocationGUID) : next.add(allocationGUID);
            return next;
        });
    };

    const toggleStrategySelected = (strategyGUID: GUID<Strategy>) => {
        setSelectedStrategyGUIDs((prev) => {
            const next = new Set(prev);
            next.has(strategyGUID) ? next.delete(strategyGUID) : next.add(strategyGUID);
            return next;
        });
    };

    const totalSelectedCount = selectedModelGUIDs.size + selectedStrategyGUIDs.size;

    const selectionLabel = (() => {
        const modelCount = selectedModelGUIDs.size;
        const strategyCount = selectedStrategyGUIDs.size;
        const modelPart = modelCount > 0 ? `${modelCount} ${modelCount === 1 ? 'model' : 'models'}` : '';
        const strategyPart =
            strategyCount > 0 ? `${strategyCount} ${strategyCount === 1 ? 'strategy' : 'strategies'}` : '';
        return [modelPart, strategyPart].filter(Boolean).join(' and ');
    })();

    const handleClearSelection = () => {
        setSelectedModelGUIDs(new Set());
        setSelectedStrategyGUIDs(new Set());
    };

    const handleConfirmBulkDelete = () => {
        selectedModelGUIDs.forEach((guid) => deleteAllocation(guid));
        selectedStrategyGUIDs.forEach((guid) => deleteStrategy(guid));
        setSelectedModelGUIDs(new Set());
        setSelectedStrategyGUIDs(new Set());
        setBulkDeleteConfirmOpen(false);
    };

    const searchLower = search.trim().toLowerCase();
    const isClaroAllocation = (allocation: Allocation) =>
        allocation.allocationType === AllocationType.SHARED && !isBlackrockAllocation(allocation);
    const selectableStrategies = useMemo(() => strategies.filter(isSelectableStrategy), [strategies]);

    const modelsMatchingSearch = useMemo(
        () => allocations.filter((a) => !searchLower || a.allocationName.toLowerCase().includes(searchLower)),
        [allocations, searchLower],
    );
    const strategiesMatchingSearch = useMemo(
        () => selectableStrategies.filter((s) => !searchLower || s.strategyName.toLowerCase().includes(searchLower)),
        [selectableStrategies, searchLower],
    );

    const showModelsSection = filter !== 'my-strategies';
    const showStrategiesSection = filter !== 'my-models';

    const modelsForFilter = useMemo(
        () =>
            modelsMatchingSearch.filter((allocation) => {
                if (filter === 'my-models') return allocation.allocationType === AllocationType.PERSONAL;
                if (filter === 'claro') return isClaroAllocation(allocation);
                if (filter === 'blackrock') return isBlackrockAllocation(allocation);
                return true;
            }),
        [modelsMatchingSearch, filter],
    );

    const strategiesForFilter = useMemo(
        () =>
            strategiesMatchingSearch.filter((strategy) => {
                if (filter === 'my-strategies') return strategy.strategyType === StrategyType.PERSONAL;
                if (filter === 'claro') return strategy.strategyType === StrategyType.CORE;
                if (filter === 'blackrock') return isBlackrockStrategy(strategy);
                return true;
            }),
        [strategiesMatchingSearch, filter],
    );

    const counts: Record<LibraryFilterKey, number> = {
        all: modelsMatchingSearch.length + strategiesMatchingSearch.length,
        'my-models': modelsMatchingSearch.filter((a) => a.allocationType === AllocationType.PERSONAL).length,
        'my-strategies': strategiesMatchingSearch.filter((s) => s.strategyType === StrategyType.PERSONAL).length,
        claro:
            modelsMatchingSearch.filter(isClaroAllocation).length +
            strategiesMatchingSearch.filter((s) => s.strategyType === StrategyType.CORE).length,
        blackrock:
            modelsMatchingSearch.filter(isBlackrockAllocation).length +
            strategiesMatchingSearch.filter(isBlackrockStrategy).length,
    };

    return (
        <Page>
            <PageTopBar />
            <LibraryTitleBar
                filter={filter}
                modelCount={allocations.length}
                strategyCount={selectableStrategies.length}
                onNewModel={handleNewModel}
                onNewStrategy={handleNewStrategy}
            />
            <PageContent>
                <div className={cn('flex flex-col', totalSelectedCount > 0 && 'pb-18')}>
                    <LibraryFilterBar
                        filter={filter}
                        onFilterChange={setFilter}
                        counts={counts}
                        showBlackrock={isBlackrockEnabled}
                        search={search}
                        onSearchChange={setSearch}
                        sort={sort}
                        onSortChange={setSort}
                    />

                    <div className="mt-6 flex flex-col gap-8">
                        {showModelsSection && (
                            <ModelsSection
                                allocations={modelsForFilter}
                                isLoading={isLoadingAllocations}
                                hasSupportRole={hasSupportRole}
                                sort={sort}
                                selectedGUIDs={selectedModelGUIDs}
                                onToggleSelect={toggleModelSelected}
                                onEdit={handleEditModel}
                                onClone={handleCloneModel}
                                onDelete={modelDelete.request}
                                onOpenStrategy={handleEditStrategy}
                            />
                        )}

                        {showStrategiesSection && (
                            <StrategiesSection
                                strategies={strategiesForFilter}
                                isLoading={isLoadingStrategies}
                                hasSupportRole={hasSupportRole}
                                sort={sort}
                                selectedGUIDs={selectedStrategyGUIDs}
                                onToggleSelect={toggleStrategySelected}
                                onEdit={handleEditStrategy}
                                onClone={handleCloneStrategy}
                                onDelete={strategyDelete.request}
                            />
                        )}

                        {!isLoadingAllocations &&
                            !isLoadingStrategies &&
                            (!showModelsSection || modelsForFilter.length === 0) &&
                            (!showStrategiesSection || strategiesForFilter.length === 0) && (
                                <div className="border-cocoa-300 flex flex-col items-center gap-2 rounded-lg border border-dashed bg-white px-6 py-16 text-center">
                                    <span className="text-cocoa-1000 text-lg font-medium">Nothing here yet</span>
                                    <span className="text-cocoa-500 max-w-sm text-sm">
                                        No models or strategies match your current filters. Try a different search or
                                        filter, or create a new one.
                                    </span>
                                </div>
                            )}
                    </div>
                </div>
            </PageContent>

            {totalSelectedCount > 0 && (
                <BulkSelectionBar selectionLabel={selectionLabel} onClear={handleClearSelection}>
                    <Button
                        variant="destructive"
                        size="sm"
                        className="flex items-center gap-1.5 text-[13px] font-medium"
                        onClick={() => setBulkDeleteConfirmOpen(true)}
                    >
                        <LuTrash2 className="h-4 w-4" /> Delete selected
                    </Button>
                </BulkSelectionBar>
            )}

            <ConfirmDeleteModal
                target={modelDelete.target}
                title="Delete Model"
                getDescription={(allocation) => (
                    <span>
                        Are you sure you want to delete <strong>{allocation.allocationName}</strong>? This action cannot
                        be undone.
                    </span>
                )}
                onConfirm={modelDelete.confirm}
                onCancel={modelDelete.cancel}
            />

            <ConfirmDeleteModal
                target={strategyDelete.target}
                title="Delete Strategy"
                getDescription={(strategy) => (
                    <span>
                        Are you sure you want to delete <strong>{strategy.strategyName}</strong>? This action cannot be
                        undone.
                    </span>
                )}
                onConfirm={strategyDelete.confirm}
                onCancel={strategyDelete.cancel}
            />

            <ConfirmModal
                open={bulkDeleteConfirmOpen}
                onOpenChange={setBulkDeleteConfirmOpen}
                title="Delete selected items"
                description={`Are you sure you want to delete ${selectionLabel}? This action cannot be undone.`}
                confirmText="Delete"
                confirmVariant="destructive"
                onConfirm={handleConfirmBulkDelete}
                onCancel={() => setBulkDeleteConfirmOpen(false)}
            />

            <EditModelDialog
                open={isModelDialogOpen}
                allocation={editingAllocation}
                onSaved={handleCancelModel}
                onCancel={handleCancelModel}
                canCreateSharedAllocation={hasSupportRole}
                existingAllocationNames={allocations.map((a) => a.allocationName)}
                availableStrategies={strategies}
            />

            <EditStrategyDialog
                open={isStrategyDialogOpen}
                strategy={editingStrategy}
                onSave={handleSaveStrategy}
                onCancel={handleCancelStrategy}
                isSaving={isSavingStrategy}
                canCreateGlobalStrategy={hasSupportRole}
                existingStrategyNames={strategies.map((s) => s.strategyName)}
                readOnly={
                    !!editingStrategy &&
                    (!editingStrategy.availableForEdits ||
                        (editingStrategy.strategyType === StrategyType.SHARED && !hasSupportRole))
                }
            />
        </Page>
    );
};
